import type {
  PrefillOptions,
  ArkSectionName,
  RequirementsRestrictions,
} from '@stripe-internal/ark';
import type {OnboardingStep} from '@stripe-internal/ark-lib';
import type {
  CollectBankAccountTokenResult,
  FinancialConnectionsSessionResult,
} from '@stripe/stripe-js';
import {ConnectElementObservableValues} from './ConnectElementObservableValues';
import type {TYPE_TO_FILTER_MESSAGES} from '../components/Transactions/filter/transactionTypes';
import type {AccountManagementSectionType} from '../components/Account/AccountManagement/AccountManagementForm/types';
import type {
  intervalEnd,
  intervalStart,
} from './ConnectJSInterface/ConnectElementDefinitions/reporting-chart';
import type {PaymentMethod, Status} from './utils/paymentFilterTypes';

export interface PaymentsListDefaultFilters {
  amount?:
    | {equals: number}
    | {greaterThan: number}
    | {lessThan: number}
    | {between: {lowerBound: number; upperBound: number}};
  date?: {before: Date} | {after: Date} | {between: {start: Date; end: Date}};
  status?: Array<Status>;
  paymentMethod?: PaymentMethod;
  metadata?: {
    key: string;
    value: string;
  };
  email?: string;
}

export interface TransactionsListDefaultFilters {
  type?: keyof typeof TYPE_TO_FILTER_MESSAGES;
}

// https://docs.google.com/document/d/1KohI_dTXnSYk2jTGj2sa7yaS7BT6bs18Ih_sR2tw3IU/edit#heading=h.z6qtovk62wrs
export interface CollectionOptions {
  fields: 'currently_due' | 'eventually_due';
  futureRequirements?: 'omit' | 'include';
  // Internal use only this still requires an API review
  requirements?: RequirementsRestrictions;
  // Let's you run account onboarding for a specific capability
  capability?: string;
}

export interface MobileBannerOptions {
  minimized?: boolean;
  renderInNav?: boolean;
}

/**
 * What a host resolves a delegated auth challenge with. Never log `secret`.
 */
export interface AuthChallengeCompletion {
  secret: string;
  metadata?: Record<string, string>;
}

/**
 * The host's answer to an `authchallengerequired` event, addressed by
 * `requestId` (see the `setAuthChallenge` supplemental-object setter).
 * `completion` is the proof `{secret}`, or null if the host dismissed the
 * challenge. `requestId: null` is a wildcard cancellation of whichever
 * request is pending, matching the stripe-react-native ACF contract.
 */
export type AuthChallengeResponse = {
  requestId: string | null;
  completion: AuthChallengeCompletion | null;
};

// Note: Supplemental objects should not contain any non-serializable fields (i.e. functions), since these fields get
// serialized to the UI layer directly via JSON.stringify in a window message
export interface IConnectElementSupplementalObject {
  // Internal
  debugUtilObjectInternalDoNotUse?: {
    prop1: string;
    prop2: {
      subprop1: number;
      subprop2: boolean;
    };
    prop3: string[];
  };
  transactionsListDefaultFilters?: TransactionsListDefaultFilters;
  arkV2DisabledSections?: ArkSectionName[];
  arkV2HiddenSections?: ArkSectionName[];
  disableBankConnections?: boolean;
  isDirectSettings?: boolean;
  hideAccountAuthenticationDetails?: boolean;
  skipInstantPayoutsConfigCheck?: boolean;

  mobileFinancialConnectionsResult?: (
    | FinancialConnectionsSessionResult
    | CollectBankAccountTokenResult
  ) & {id: string};
  // This is used to turn on the ORR M5 UX in the account management component
  // Goal is to remove this once ARKv2 direct settings is GA
  isOrrM5?: boolean;
  prefillOptions?: PrefillOptions;
  // Used in AccountOnboarding to control which composition and/or
  // section to render, allowing consumers to implement navigation.
  step?: OnboardingStep;
  sectionView?: AccountManagementSectionType;

  // Used in HostedDashboardNotificationBanner to control the display of the mobile banner
  mobileBannerOptions?: MobileBannerOptions;

  // Used in Payments to set default filters
  paymentsListDefaultFilters?: PaymentsListDefaultFilters;

  // Stripe Tax embedded component customization RFC
  displayCountries?: string[];
  displayHeadOfficeCountries?: string[];
  hideProductTaxCodeSelector?: boolean;
  hideDescription?: boolean;
  disabled?: boolean;
  initialTaxCode?: string;

  // API reviewed
  // Preview
  appData?: Record<string, string>;

  // Private preview API reviewed for ReportingChart
  [intervalStart]?: Date;
  [intervalEnd]?: Date;

  // GA
  collectionOptions?: CollectionOptions;

  // Answer to AccountOnboarding's authchallengerequired event, for hosts
  // (e.g. manage-mobile-app) that run their own ACF challenge UI instead of
  // delegating to this component's built-in ACF flow.
  authChallenge?: AuthChallengeResponse;
}

export type SupplementalObjectKey = keyof IConnectElementSupplementalObject;

export type SupplementalObjectValue =
  IConnectElementSupplementalObject[SupplementalObjectKey];

export class ConnectElementSupplementalObjects extends ConnectElementObservableValues<IConnectElementSupplementalObject> {}
